import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  Mail,
  Globe,
  Sparkles,
} from "lucide-react";

const STATUS_TABS = ["pending", "verified", "rejected", "all"];

export default function AdminEmployers() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (!user.is_admin) { toast.error(t("admin.access_denied")); nav("/dashboard"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, authLoading, user]);

  const load = () => {
    setItems(null);
    const url = tab === "all" ? "/admin/employers" : `/admin/employers?status=${tab}`;
    api.get(url).then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  const decide = async (userId, action) => {
    setBusy(userId);
    try {
      await api.post(`/admin/employers/${userId}/verify`, {
        action,
        notes: notes[userId] || "",
      });
      toast.success(action === "verified" ? t("admin.approved") : t("admin.rejected"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.error"));
    } finally {
      setBusy(null);
    }
  };

  const rerun = async (userId) => {
    setBusy(userId);
    try {
      await api.post(`/admin/employers/${userId}/rerun-verification`, {});
      toast.success(t("admin.rerun_started"));
      setTimeout(load, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.error"));
    } finally {
      setBusy(null);
    }
  };

  if (authLoading || !user || !user.is_admin) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="admin-page">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="mec-overline">{t("admin.overline")}</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">{t("admin.title")}</h1>
        </div>
      </div>
      <p className="text-sm text-slate-600 mb-6">{t("admin.subtitle")}</p>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="grid grid-cols-4 max-w-md">
          {STATUS_TABS.map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`tab-${s}`}>
              {t(`admin.tab_${s}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {items === null && <div className="text-slate-500 text-sm">Loading...</div>}
      {items !== null && items.length === 0 && (
        <div className="mec-card p-12 text-center text-slate-500">
          <Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          {t("admin.empty")}
        </div>
      )}

      <div className="space-y-3">
        {(items || []).map((p) => (
          <EmployerRow
            key={p.user_id}
            p={p}
            busy={busy === p.user_id}
            notes={notes[p.user_id] || ""}
            onNotesChange={(v) => setNotes((n) => ({ ...n, [p.user_id]: v }))}
            onApprove={() => decide(p.user_id, "verified")}
            onReject={() => decide(p.user_id, "rejected")}
            onRerun={() => rerun(p.user_id)}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

function EmployerRow({ p, busy, notes, onNotesChange, onApprove, onReject, onRerun, t }) {
  const score = p.verification_score;
  const verdict = p.verification_verdict;
  const reasons = p.verification_reasons || [];
  const sirene = p.sirene_lookup || {};

  const scoreColor =
    score == null ? "text-slate-500"
    : score >= 70 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : score >= 40 ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-rose-700 bg-rose-50 border-rose-200";

  const statusBadge = {
    verified: { icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: t("admin.status_verified") },
    pending: { icon: Clock, color: "bg-slate-50 text-slate-700 border-slate-200", label: t("admin.status_pending") },
    rejected: { icon: XCircle, color: "bg-rose-50 text-rose-700 border-rose-200", label: t("admin.status_rejected") },
  }[p.verification_status] || { icon: Clock, color: "bg-slate-50 text-slate-700 border-slate-200", label: p.verification_status || "—" };

  const StatusIcon = statusBadge.icon;

  return (
    <div className="mec-card p-5" data-testid={`employer-row-${p.user_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-display text-lg font-bold text-slate-900">{p.company_name || "—"}</h3>
            <Badge className={`${statusBadge.color} text-[10px]`} data-testid="row-status-badge">
              <StatusIcon className="w-3 h-3 mr-1" /> {statusBadge.label}
            </Badge>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
            <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {p.contact_email}</div>
            <div className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> {p.user_country}</div>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">N°:</span>
              <code className="text-slate-700">{p.registration_number || "—"}</code>
            </div>
            {p.website && (
              <a href={p.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#004EEB] hover:underline">
                <ExternalLink className="w-3 h-3" /> {t("admin.visit_website")}
              </a>
            )}
          </div>
        </div>

        {score != null && (
          <div className={`px-3 py-1.5 rounded-md border text-center ${scoreColor}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-75">{t("admin.ai_score")}</div>
            <div className="text-xl font-bold leading-tight">{score}</div>
            <div className="text-[10px] opacity-75">{verdict || ""}</div>
          </div>
        )}
      </div>

      {/* SIRENE result */}
      {p.sirene_lookup && (
        <div className="mt-3 p-2 rounded bg-slate-50 border border-slate-200 text-xs">
          <div className="font-semibold text-slate-700 mb-0.5">🇫🇷 SIRENE INSEE</div>
          {sirene.found ? (
            <div className="text-emerald-700">
              ✓ {t("admin.sirene_found")} : <strong>{sirene.company_name || "—"}</strong>
              {sirene.active ? <span className="ml-2 text-emerald-700">• {t("admin.sirene_active")}</span> : <span className="ml-2 text-amber-700">• {t("admin.sirene_inactive")}</span>}
            </div>
          ) : (
            <div className="text-rose-700">✗ {t("admin.sirene_not_found")}</div>
          )}
          {p.siret_luhn_valid != null && (
            <div className={`mt-0.5 ${p.siret_luhn_valid ? "text-emerald-700" : "text-rose-700"}`}>
              {p.siret_luhn_valid ? "✓" : "✗"} {t("admin.luhn")} : {p.siret_luhn_valid ? t("admin.valid") : t("admin.invalid")}
            </div>
          )}
        </div>
      )}

      {/* AI reasons */}
      {reasons.length > 0 && (
        <div className="mt-3 p-2 rounded bg-blue-50 border border-blue-200 text-xs">
          <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-500" /> {t("admin.ai_analysis")}
          </div>
          <ul className="text-slate-700 space-y-0.5 ml-3 list-disc">
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {p.verification_notes && (
        <div className="mt-3 text-xs text-slate-600 italic">
          {t("admin.previous_notes")}: {p.verification_notes}
        </div>
      )}

      {/* Action area (only for pending / all to allow re-decision) */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <Textarea
          rows={2}
          placeholder={t("admin.notes_placeholder")}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          className="text-xs mb-2"
          data-testid={`notes-${p.user_id}`}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={onApprove}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            data-testid={`approve-${p.user_id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t("admin.approve")}
          </Button>
          <Button
            size="sm"
            onClick={onReject}
            disabled={busy}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50 text-xs"
            data-testid={`reject-${p.user_id}`}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" /> {t("admin.reject")}
          </Button>
          <Button
            size="sm"
            onClick={onRerun}
            disabled={busy}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50 text-xs"
            data-testid={`rerun-${p.user_id}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> {t("admin.rerun")}
          </Button>
        </div>
      </div>
    </div>
  );
}
